import React from 'react';
import { Layout, Typography, Button, Space, Segmented, Dropdown, theme } from 'antd';
import { GlobalOutlined, MoonOutlined, SunOutlined, HomeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation, useLanguage, type Language } from '../i18n/i18n';
import { useThemeContext } from '../contexts/ThemeContext';

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;

interface PageLayoutProps {
  children: React.ReactNode;
  showHomeButton?: boolean;
  showTitle?: boolean;
  footer?: React.ReactNode;
}

const PageLayout: React.FC<PageLayoutProps> = ({
  children,
  showHomeButton = true,
  showTitle = false,
  footer,
}) => {
  const { t, language } = useTranslation();
  const { setLanguage } = useLanguage();
  const { theme: currentTheme, setTheme: setCurrentTheme } = useThemeContext();
  const { token } = theme.useToken();
  const navigate = useNavigate();

  const languageOptions = [
    {
      key: 'en-US',
      label: t('landing.navbar.english'),
    },
    {
      key: 'pt-BR',
      label: t('landing.navbar.portuguese'),
    },
  ];

  const handleLanguageChange = ({ key }: { key: string }) => {
    setLanguage(key as Language);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          background: 'transparent',
          padding: `${token.padding}px ${token.paddingLG}px`,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {showHomeButton ? (
          <Button
            type="text"
            icon={<HomeOutlined />}
            onClick={() => navigate('/')}
            style={{ padding: `${token.paddingXS}px ${token.paddingXS}px` }}
          >
            {t('common.home')}
          </Button>
        ) : showTitle ? (
          <Title level={3} style={{ margin: 0, color: token.colorPrimary }}>
            XL Master
          </Title>
        ) : null}
        <Space>
          <Segmented
            value={currentTheme}
            onChange={value => setCurrentTheme(value as 'light' | 'dark')}
            options={[
              {
                value: 'dark',
                icon: <MoonOutlined />,
              },
              {
                value: 'light',
                icon: <SunOutlined />,
              },
            ]}
          />
          <Dropdown
            menu={{
              items: languageOptions,
              onClick: handleLanguageChange,
              selectedKeys: [language],
            }}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<GlobalOutlined />}
              style={{ padding: `${token.paddingXS}px ${token.paddingXS}px` }}
            />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: token.paddingLG }}>{children}</Content>

      <Footer style={{ textAlign: 'center' }}>
        {footer || <Text type="secondary">{t('landing.footer.text')}</Text>}
      </Footer>
    </Layout>
  );
};

export default PageLayout;

